import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription, take, zip } from 'rxjs';
import { DfMeDropdownComponent } from '../../../shared/components/df-me-dropdown/df-me-dropdown.component';
import { JSONExportHeader, JSONExportService } from '../../../shared/_services/JSONExport.service';
import { DiscModalViewerComponent } from '../../shared/components/disc-modal-viewer/disc-modal-viewer.component';
import { StmtIdBtnService } from '../../shared/components/stmt-id-btn/stmt-id-btn.service';
import { DSTSharedService } from '../../shared/dst-shared.service';
import { DSTChildFormState } from '../../shared/models/dst-child-form-state.model';
import { DSTStatement } from '../../shared/models/dst-statement.model';
import { CreateDisclosureService, DSTTaxYear } from '../../statement-tracker/create-disclosure/create-new/create-disclosure.service';
import { DSTDynamicComponentResolverService } from '../../statement-tracker/create-disclosure/shared/dst-dynamic-component-resolver.service';
import { TrackerTabsService } from '../../statement-tracker/tracker-tabs.service';
import { ReportsService } from '../reports.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { generalConfigFromGlobal, hydratedGlobalParams } from '../../../shared/workspace-element-context';


import {
  buildDstTaxYearItems,
  resolveDstTaxYearFromEvent,
  setDstTaxYearAriaLabel,
  syncDstTaxYearItem
} from '../../shared/utils/dst-tax-year-appkit.support';
import { getAppkitDropdownSelection } from '../../../shared/utils/appkit-dropdown.util';

@Component({
    selector: 'gtw-sec351-report',
    templateUrl: './sec-351-report.component.html',
    styleUrls: ['../../shared/styles/dst-shared.scss', '../../shared/mat-table/dst-mat-table.scss', './sec-351-report.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class Sec351ReportComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);


  @ViewChild(DiscModalViewerComponent) modalViewerComponent!: DiscModalViewerComponent;
  @ViewChild(DfMeDropdownComponent) dfMeDropdownComponent!: DfMeDropdownComponent;
  @ViewChild(MatSort) matSort!: MatSort;

  data: any[] = [];
  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = [];
  cols: any[] = [
    {
      field: 'STATEMENTID',
      header: 'Statement Id',
      hidden: false,
      width: '145px'
    },
    {
      field: 'PROJECT_NAME',
      header: 'Project',
      hidden: false,
      width: '105px'
    },
    {
      field: 'PRIMARYLEID',
      header: 'Transferee (Primary LEID)',
      hidden: false,
      width: '155px'
    },
    {
      field: 'PRIMARYENTITY',
      header: 'Transferee Name',
      hidden: false,
      width: '180px'
    },
    {
      field: 'PRIMARYBUSINESS',
      header: 'Transferee Business/ME',
      hidden: false,
      width: '260px'
    },
    {
      field: 'FILLING_GROUP',
      header: 'Filling group',
      hidden: false,
      width: '145px'
    },
    {
      field: 'SECONDARYLEID',
      header: 'Transferor (Secondary LEID)',
      hidden: false,
      width: '175px'
    },
    {
      field: 'SECONDARYENTITY',
      header: 'Transferor Name',
      hidden: false,
      width: '260px'
    },
    {
      field: 'DATE_OF_TRANSFER',
      header: 'Date of Transfer',
      hidden: false,
      width: '170px'
    },
    {
      field: 'IMPORTATIONPROPERTYDESC',
      header: 'Importation property transferred in a loss importation transaction',
      hidden: false,
      width: '275px'
    },
    {
      field: 'LOSSDUPLICATIONPROPERTYDESC',
      header: 'Loss duplication property',
      hidden: false,
      width: '236px'
    },
    {
      field: 'GAINLOSSPROPERTYDESC',
      header: 'Property with respect to which any gain or loss was recognized on the transfer',
      hidden: false,
      width: '330px'
    },
    {
      field: 'DESCRIPTION',
      header: 'Property Transferred',
      hidden: false,
      width: '375px'
    },
    {
      field: 'BASIS_AMOUNT',
      header: 'Basis Amount',
      hidden: false,
      width: '155px'
    },
    {
      field: 'BASIS_CURRENCY',
      header: 'Basis Currency',
      hidden: false,
      width: '155px'
    },
    {
      field: 'BASISTYPE',
      header: 'Basis Type',
      hidden: false,
      width: '125px'
    },
    {
      field: 'FMV_AMOUNT',
      header: 'FMV Amount',
      hidden: false,
      width: '150px'
    },
    {
      field: 'FMV_CURRENCY',
      header: 'FMV Currency',
      hidden: false,
      width: '155px'
    },
    {
      field: 'STOCK_TYPE',
      header: 'Stock Type',
      hidden: false,
      width: '130px'
    },
    {
      field: 'SHARE_X',
      header: 'Before Transfer: Shares Outstanding',
      hidden: false,
      width: '185px'
    },
    {
      field: 'PERCENTAGE_X',
      header: 'Before Transfer: %Ownership by Transferor',
      hidden: false,
      width: '235px'
    },
    {
      field: 'SHARE_Y',
      header: 'After Transfer: Shares Outstanding',
      hidden: false,
      width: '180px'
    },
    {
      field: 'PERCENTAGE_Y',
      header: 'After Transfer: %Ownership by Transferor',
      hidden: false,
      width: '220px'
    },
    {
      field: 'LIABILITIES_DESC',
      header: 'Liabilities Assumed by Transferee',
      hidden: false,
      width: '180px'
    },
    {
      field: 'LIABILITIESAMT',
      header: 'Liability Amount',
      hidden: false,
      width: '170px'
    },
    {
      field: 'IS_362E',
      header: '362(e) Diagnostic',
      hidden: false,
      width: '180px'
    },
    {
      field: 'IS_304',
      header: 'Is 304?',
      hidden: false,
      width: '100px'
    },
    {
      field: 'TARGETLEID',
      header: 'Target LEID',
      hidden: false,
      width: '135px'
    },
    {
      field: 'TARGETNAME',
      header: 'Target Name',
      hidden: false,
      width: '180px'
    },
    {
      field: 'NOOFSHARES',
      header: 'No. of Shares Transferred',
      hidden: false,
      width: '135px'
    },
    {
      field: 'STOCKTRANSFERRED',
      header: 'Stock Transferred',
      hidden: false,
      width: '180px'
    },
    {
      field: 'TRANSFEREESNOTE',
      header: 'Property In Exchange',
      hidden: false,
      width: '205px'
    },
    {
      field: 'AMTFOR351TRANSFER',
      header: 'Amount Exchanged',
      hidden: false,
      width: '195px'
    },
    {
      field: 'TRANSDESCRIPTION',
      header: 'Transaction Description',
      hidden: false,
      width: '225px'
    },
  ];
  loading: boolean = true;
  taxyearLoaded: boolean = false;
  excelExportFileName: string;

  taxYears: DSTTaxYear[] = [];
  taxYearItems: { value: number; label: string }[] = [];
  selectedTaxYearItem: any = {};
  taxYear: DSTTaxYear | undefined;
  selectedMes: string = '';
  activeRow!: DSTStatement;
  showModalViewer: boolean = false;
  private onDisCancelSubs!: Subscription;
  private contextApplied = false;

  constructor(private cDSerivce: CreateDisclosureService,
    private dstSharedService: DSTSharedService,
    private stmtIdBtnService: StmtIdBtnService,
    private trackerTabsService: TrackerTabsService,
    private dCRService: DSTDynamicComponentResolverService,
    private jsonExportService: JSONExportService,
    private reportsService: ReportsService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService) {

    this.excelExportFileName = 'Reports-351_Report_' + this.dstSharedService.getExcelExportDateString();
    this.displayedColumns = this.cols.filter((c) => !c.hidden).map((c) => c.field);
  }

  ngOnInit(): void {
    this.onDisCancelSubs = this.dCRService.onDisclosureCancel.subscribe(() => {
      this.trackerTabsService.destroyModalTab();
      this.showModalViewer = false;
    });

    this.applyWorkspaceContext();
    this.onDisCancelSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngAfterViewInit(): void {
    this.connectMatTableSort();
  }

  ngOnDestroy(): void {
    this.onDisCancelSubs?.unsubscribe();
  }

  getColStyle(col: { width?: string }): Record<string, string> {
    const width = col.width || '100px';
    return { width, minWidth: width, maxWidth: width };
  }

  onMatSortChange(): void {
    this.connectMatTableSort();
  }

  private connectMatTableSort(): void {
    if (this.matSort) {
      this.dataSource.sort = this.matSort;
    }
  }

  private applyWorkspaceContext(): void {
    if (this.contextApplied) return;
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    const gp = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    const user = this.gtwApp.USER_SETTINGS?.user || {};
    const clientKey = String(user.client?.client_key || user.client_key || '');
    const ssoId = String(user.sso_id || '');
    const roleLevel = Number(this.gtwWorkspace.workspaceFactory?.activeScreen?.parent?.access_level || 0);
    const taxYear = String(gp.tax_year || '');
    const scenario = String(gp.scenario || '');
    if (!clientKey && !ssoId) return;
    this.contextApplied = true;
    this.dstSharedService.setClientKey(clientKey);
    this.dstSharedService.setDSTUser(ssoId, roleLevel);
    this.dstSharedService.setSystemLockUnlockStatus(false, roleLevel);
    this.dstSharedService.setTaxYearScenario(taxYear, scenario);
    this.dstSharedService.setBaseURLs(gc);
    const getTaxYears$ = this.cDSerivce.getTaxYears();
    zip(getTaxYears$).pipe(take(1)).subscribe((data) => {
      this.taxYears = data[0];
      this.setTaxYear(taxYear);
      this.dfMeDropdownComponent.loadMEsCustom(this.taxYear?.text.toString()!);
      this.loadReport();
    });
  }

  onTaxYearChange(): void {
    this.taxyearLoaded = true;
    this.dfMeDropdownComponent.loadMEsCustom(this.taxYear?.text.toString()!);
  }

  setTaxYear(taxYear: string): void {
    this.taxYear = this.taxYears.find((item: any) => item.text.toString() === taxYear);
    this.taxYearItems = buildDstTaxYearItems(this.taxYears);
    this.selectedTaxYearItem = syncDstTaxYearItem(this.taxYearItems, this.taxYear, taxYear);
    this.taxyearLoaded = true;
  }

  onTaxYearChanged(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedTaxYearItem = selected || this.selectedTaxYearItem;
    const matched = resolveDstTaxYearFromEvent(event, this.taxYears);
    if (matched) {
      this.taxYear = matched;
      this.onTaxYearChange();
    }
  }

  onTaxYearSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedTaxYearItem = selected;
    }
  }

  setTaxYearAriaLabel(item: any): string {
    return setDstTaxYearAriaLabel(item);
  }

  // setScenarios(taxYear: string): void {
  //   this.scenarios = [ { text:'Select Scenario', value: null } ];

  //   const selectedTaxYear = this.taxYearScenarioData.values.find((item: any) => { return item.value.toString() === taxYear; });
  //   selectedTaxYear?.scenarios.filter((ele : any) => ele.is_locked === 'N' && ele.channel_key.toString() === this.selectedChannelKey).forEach((item : any) => {
  //     this.scenarios.push({
  //       text: item.scenario_desc,
  //       value: item.scenario,
  //       selected: item.selected
  //     });
  //   });
  //   this.scenario = this.scenarios.find((item: any) => { return item.value === selectedTaxYear?.scenario; });
  //   this.scenarioLoaded = true;
  // }

  selectedMeChanged(selected: string): void {
    this.selectedMes = selected;
  }

  loadReport(): void {
    this.data = [];
    this.dataSource.data = [];
    this.loading = true;
    this.reportsService.loadSection351Report(this.taxYear?.text.toString()!, this.selectedMes || '').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.data = data;
      this.data?.forEach((element) => {
        element.statementId = element.STATEMENTID;
        element.taxYearId = element.TAXYEARID;
        element.formId = element.FORMID;
        element.formName = element.FORMNAME;
      });
      this.dataSource.data = this.data;
      this.connectMatTableSort();
    }, (error: HttpErrorResponse) => {
      console.error(error);
      this.data = [];
      this.dataSource.data = [];
      this.dstSharedService.showAlert('error', 'Something went wrong while loading the N/A Report grid data, please try again!');
    }).add(() => {
      this.loading = false;
    });
  }

  showViewUpdateStatement(rowData: any): void {
    this.activeRow = rowData;
    this.dstSharedService.setTableActionStatus(true);
    this.dstSharedService.setIsFiled(rowData.IS_FILED!);
    this.stmtIdBtnService.getChildStatements(rowData.STATEMENTID).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((childFormStates: DSTChildFormState[]) => {
      this.showModalViewer = true;
      setTimeout(() => {
        this.modalViewerComponent.showMore('dst-disclosure-viewer', childFormStates);
      });
    }, (eMsg: string) => {
      console.log(eMsg);
      this.dstSharedService.showAlert('error', eMsg);
    }).add(() => {
      this.dstSharedService.setTableActionStatus(false);
    });
  }

  afterModalClosed(): void {
    this.trackerTabsService.destroyModalTab();
    this.showModalViewer = false;
  }

  exportToExcel(): void {
    const headersMap: JSONExportHeader[] = [
      {
        field: 'STATEMENTID',
        header: 'Statement Id',
      },
      {
        field: 'PROJECT_NAME',
        header: 'Project',
      },
      {
        field: 'PRIMARYLEID',
        header: 'Transferee (Primary LEID)',
      },
      {
        field: 'PRIMARYENTITY',
        header: 'Transferee Name',
      },
      {
        field: 'PRIMARYBUSINESS',
        header: 'Transferee Business/ME',
      },
      {
        field: 'FILLING_GROUP',
        header: 'Filling group',
      },
      {
        field: 'SECONDARYLEID',
        header: 'Transferor (Secondary LEID)',
      },
      {
        field: 'SECONDARYENTITY',
        header: 'Transferor Name',
      },
      {
        field: 'DATE_OF_TRANSFER',
        header: 'Date of Transfer',
      },
      {
        field: 'IMPORTATIONPROPERTYDESC',
        header: 'Importation property transferred in a loss importation transaction',
      },
      {
        field: 'LOSSDUPLICATIONPROPERTYDESC',
        header: 'Loss duplication property',
      },
      {
        field: 'GAINLOSSPROPERTYDESC',
        header: 'Property with respect to which any gain or loss was recognized on the transfer',
      },
      {
        field: 'DESCRIPTION',
        header: 'Property Transferred',
      },
      {
        field: 'BASIS_AMOUNT',
        header: 'Basis Amount',
        format: 'number',
      },
      {
        field: 'BASIS_CURRENCY',
        header: 'Basis Currency',
      },
      {
        field: 'BASISTYPE',
        header: 'Basis Type',
      },
      {
        field: 'FMV_AMOUNT',
        header: 'FMV Amount',
        format: 'number',
      },
      {
        field: 'FMV_CURRENCY',
        header: 'FMV Currency',
      },
      {
        field: 'STOCK_TYPE',
        header: 'Stock Type',
      },
      {
        field: 'SHARE_X',
        header: 'Before Transfer: Shares Outstanding',
        format: 'number',
      },
      {
        field: 'PERCENTAGE_X',
        header: 'Before Transfer: %Ownership by Transferor',
      },
      {
        field: 'SHARE_Y',
        header: 'After Transfer: Shares Outstanding',
        format: 'number',
      },
      {
        field: 'PERCENTAGE_Y',
        header: 'After Transfer: %Ownership by Transferor',
      },
      {
        field: 'LIABILITIES_DESC',
        header: 'Liabilities Assumed by Transferee',
      },
      {
        field: 'LIABILITIESAMT',
        header: 'Liability Amount',
        format: 'number',
      },
      {
        field: 'IS_362E',
        header: '362(e) Diagnostic',
      },
      {
        field: 'IS_304',
        header: 'Is 304?',
      },
      {
        field: 'TARGETLEID',
        header: 'Target LEID',
      },
      {
        field: 'TARGETNAME',
        header: 'Target Name',
      },
      {
        field: 'NOOFSHARES',
        header: 'No. of Shares Transferred',
        format: 'number',
      },
      {
        field: 'STOCKTRANSFERRED',
        header: 'Stock Transferred',
      },
      {
        field: 'TRANSFEREESNOTE',
        header: 'Property In Exchange',
      },
      {
        field: 'AMTFOR351TRANSFER',
        header: 'Amount Exchanged',
        format: 'number',
      },
      {
        field: 'TRANSDESCRIPTION',
        header: 'Transaction Description',
      }
    ];

    this.jsonExportService.exportToExcel(this.excelExportFileName, this.data, headersMap);
  }

}
